import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { TakeawaysProgress } from '@scoutaround/script-breakdown-shared';

import { api, type ScriptProgress } from './api.js';

/** Twenty minutes at a poll every 1.5 seconds — see `refetchInterval`. */
const MAX_POLLS = 800;

/** A run that will not change again: the worker's three terminal states. */
export function runFinished(status: string | undefined): boolean {
  return status === 'COMPLETED' || status === 'PARTIAL' || status === 'FAILED';
}

/**
 * THE ONE PLACE A SCREEN ASKS "IS THE BREAKDOWN STILL GOING?"
 *
 * Three screens polled the pipeline run with three hand-written intervals,
 * and the rule that matters — re-read your data ONCE when the run ends,
 * because the last in-flight answer is the one fetched before the
 * requirements were written — lived in only one of them. It lives here now.
 *
 * Polled only while something is moving, and stopped by the answer rather
 * than by a timer: a run sits at QUEUED until the worker picks it up, which
 * is "still going", and anything not finished is treated the same way.
 * `onFinished` fires exactly once, on the transition from in flight to
 * finished, never on a screen that opened after the run was already over.
 */
export function useScriptRun(
  scriptVersionId: string | null,
  options: { onFinished?: () => void } = {},
): RunPoll<ScriptProgress> {
  return useRunPoll(['script-progress', scriptVersionId], () => api.scriptProgress(scriptVersionId as string), scriptVersionId, options);
}

/**
 * THE SAME QUESTION FOR KEY TAKEAWAYS. A takeaways run has its own stage and its own poll
 * (`takeaways-progress`): the breakdown's progress never reports it, so the breakdown never reads
 * as running while takeaways are read. One analysis at a time per draft still holds on the
 * server, so a screen that offers a reading asks both hooks before it does.
 */
export function useTakeawaysRun(
  scriptVersionId: string | null,
  options: { onFinished?: () => void } = {},
): RunPoll<TakeawaysProgress> {
  return useRunPoll(['takeaways-progress', scriptVersionId], () => api.takeawaysProgress(scriptVersionId as string), scriptVersionId, options);
}

export interface RunPoll<P> {
  progress: P | null;
  status: string | null;
  inFlight: boolean;
  isPending: boolean;
  error: Error | null;
  givenUp: boolean;
}

function useRunPoll<P extends { readonly runId: string; readonly status: string }>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<P | null>,
  scriptVersionId: string | null,
  options: { onFinished?: () => void },
): RunPoll<P> {
  const query = useQuery({
    queryKey,
    queryFn,
    enabled: scriptVersionId !== null,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      if (runFinished(data.status)) return false;

      /**
       * AND IT STOPS EVENTUALLY, EVEN IF THE RUN NEVER DOES.
       *
       * The stop condition was the run reaching a terminal status, which is
       * correct right up to the case where it never does — a worker killed
       * mid-job writes no FAILED, and a status this build does not recognise
       * reads as "still going" forever. A screen left open then polled every
       * 1.5s for as long as the tab lived.
       *
       * The ceiling is generous on purpose. Extraction on a feature is minutes,
       * not seconds, so this must not give up on a slow but healthy run; twenty
       * minutes is well past any real one and far short of forever.
       */
      return q.state.dataUpdateCount > MAX_POLLS ? false : 1500;
    },
  });

  const progress = query.data ?? null;
  const inFlight = progress !== null && !runFinished(progress.status);

  // Counted here rather than read off the query: the observer result exposes
  // `dataUpdatedAt`, not the update count the interval callback sees.
  const polls = useRef(0);
  polls.current = progress === null ? 0 : polls.current + 1;

  // The callback is read through a ref so a consumer can pass a fresh
  // closure every render without re-arming the effect.
  const onFinished = useRef(options.onFinished);
  onFinished.current = options.onFinished;
  const wasInFlight = useRef(false);
  // The run the screen saw first; `undefined` until any answer arrives.
  const seenRunId = useRef<string | null | undefined>(undefined);
  const runId = progress?.runId ?? null;
  const hasProgress = progress !== null;
  // "No run yet" (a `null` answer) is an answer too: a draft with no run, whose FIRST run then
  // finishes between two polls, must still be re-read. Only "not asked yet" leaves it unknown.
  const answered = query.data !== undefined;

  useEffect(() => {
    const previousRun = seenRunId.current;
    if (answered) seenRunId.current = runId;

    // Seen moving, now over: the ordinary end of a run.
    if (wasInFlight.current && !inFlight) onFinished.current?.();
    // A NEW run that was already over the first time the screen saw it. A fast
    // run (the mock adapter on dev, a short real one between two polls) can
    // finish before any answer shows it moving; without this the screen kept
    // the answer it read before the run wrote anything and said the breakdown
    // found nothing (dev smoke test, 2026-10-30). The run that was there when
    // the screen opened is never one: that has nothing new to re-read.
    else if (!inFlight && hasProgress && previousRun !== undefined && previousRun !== runId) {
      onFinished.current?.();
    }

    wasInFlight.current = inFlight;
  }, [inFlight, runId, hasProgress, answered]);

  return {
    progress,
    status: progress?.status ?? null,
    inFlight,
    isPending: query.isPending,
    /**
     * Returned so a screen can tell "no run has started" from "we asked and
     * could not find out". Every consumer treated a failed poll as the former,
     * which on this screen renders as nothing at all — and now that one hook
     * feeds them all, this is the place to stop that being inherited again.
     */
    error: query.error,
    givenUp: inFlight && polls.current > MAX_POLLS,
  };
}
